import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { CheckCircleOutline } from "@mui/icons-material";
import TextDetectionViewer from "./TextDetectionViewer";

const StatusPage = () => {
  const { id: inferenceId } = useParams();
  const [status, setStatus] = useState("Processing...");
  const [renderTextViewer, setRenderTextViewer] = useState(false);
  const [pageProgress, setPageProgress] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageStatus, setPageStatus] = useState("");
  const [queuePosition, setQueuePosition] = useState(null);
  const [queueSize, setQueueSize] = useState(null);
  const [key, setKey] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_API_URL || "https://lipikar.cse.iitd.ac.in/api/ocr";

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/upload?id=${inferenceId}&processing_status_only=1`);
        const result = await response.json();

        if (!result.success) {
          return;
        }

        const processingStatus = JSON.parse(result.result.processingStatus);
        const progress = processingStatus.pagesProgress;
        const statusString = processingStatus.statusString;
        const queue = result.result.queue || null;

        setPageStatus(statusString);
        setPageProgress(progress);
        setQueuePosition(queue?.position ?? null);
        setQueueSize(queue?.size ?? null);

        if (progress) {
          const [current, total] = progress.split("/").map(Number);
          setCurrentPage(current);
          setTotalPages(total);
        }

        if (progress === "" && statusString === "") {
          clearInterval(intervalId);
          setStatus("Completed");
          setRenderTextViewer(true);
          setKey((prevKey) => prevKey + 1);
          return;
        }

        if (processingStatus.statusCode >= 5) {
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error polling for OCR status:", error);
        clearInterval(intervalId);
        setStatus("Error checking status");
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [inferenceId, BACKEND_URL]);

  const renderProgressBar = () => {
    if (!totalPages) {
      return null;
    }

    const progress = (currentPage / totalPages) * 100;

    return (
      <Box sx={{ width: "100%", mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Processing pages: {pageProgress}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 2 }} />
      </Box>
    );
  };

  if (renderTextViewer) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            px: { xs: 1.25, sm: 2 },
            py: { xs: 1, sm: 0.75 },
            backgroundColor: "#17324d",
            borderBottom: "1px solid #27496a",
            flexShrink: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutline sx={{ fontSize: 18, color: "#7bd389" }} />
            <Typography variant="body2" sx={{ color: "#f5f7fa", fontWeight: 700, fontSize: "0.85rem" }}>
              OCR Completed
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              window.location.href = "/viewer";
            }}
            sx={{
              color: "#d7dfeb",
              borderColor: "#5c7a99",
              textTransform: "none",
              fontSize: "0.75rem",
              py: 0.25,
              width: { xs: "100%", sm: "auto" },
              "&:hover": { borderColor: "#a9c3da", color: "#ffffff" },
            }}
          >
            Back to Home
          </Button>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <TextDetectionViewer key={key} />
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5, md: 8 }, px: { xs: 2, sm: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2.25, sm: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: { xs: 2.5, sm: 3 },
            background: "linear-gradient(135deg, #ffffff 0%, #f7fbff 100%)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              textAlign: "center",
              color: "text.primary",
              fontWeight: 700,
              fontSize: { xs: "1.35rem", sm: "1.5rem" },
              lineHeight: 1.3,
            }}
          >
            OCR Status: {status}
          </Typography>

          {queuePosition ? (
            <Chip
              color="primary"
              label={`Queue position: ${queuePosition}${queueSize ? ` of ${queueSize}` : ""}`}
              sx={{ mb: 2, fontWeight: 700, maxWidth: "100%", ".MuiChip-label": { display: "block", whiteSpace: "normal" } }}
            />
          ) : null}

          {pageStatus ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: "center", lineHeight: 1.7, wordBreak: "break-word" }}
            >
              {pageStatus}
            </Typography>
          ) : null}

          {queuePosition && currentPage === 0 ? (
            <Alert severity="info" sx={{ width: "100%", mb: 3, alignItems: "center" }}>
              Your upload is waiting in the queue. This page will refresh automatically when processing begins.
            </Alert>
          ) : null}

          {renderProgressBar()}

          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              window.location.href = "/viewer";
            }}
            sx={{ minWidth: { xs: "100%", sm: 160 }, textTransform: "none", borderRadius: 2 }}
          >
            Back to Home
          </Button>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default StatusPage;
